import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Filter, X } from "lucide-react";

const CATS = [
  { key: "all", label: "All" },
  { key: "rehearsal", label: "Rehearsal" },
  { key: "photo", label: "Photo / Video" },
  { key: "meeting", label: "Meeting" },
  { key: "fitness", label: "Fitness" },
  { key: "event", label: "Event" },
  { key: "popup", label: "Pop-up" },
  { key: "other", label: "Other" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [city, setCity] = useState(params.get("city") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [maxPrice, setMaxPrice] = useState(Number(params.get("max_price") || 200));
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const query = {};
      if (city) query.city = city;
      if (category && category !== "all") query.category = category;
      if (maxPrice && maxPrice < 500) query.max_price = maxPrice;
      if (q) query.q = q;
      const { data } = await api.get("/listings", { params: query });
      setResults(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchResults(); /* eslint-disable-next-line */ }, []);

  const apply = () => {
    const p = new URLSearchParams();
    if (city) p.set("city", city);
    if (category !== "all") p.set("category", category);
    if (maxPrice < 500) p.set("max_price", String(maxPrice));
    if (q) p.set("q", q);
    setParams(p);
    fetchResults();
  };

  const clear = () => {
    setCity(""); setCategory("all"); setMaxPrice(200); setQ("");
    setParams(new URLSearchParams());
    setTimeout(fetchResults, 0);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
      {/* Sidebar filters */}
      <aside className="lg:sticky lg:top-24 h-max border border-neutral-200 bg-white rounded-xl p-6" data-testid="filter-sidebar">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 font-display font-bold text-lg"><Filter className="w-4 h-4" /> Filters</div>
          <button onClick={clear} className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-[#FF4500]" data-testid="clear-filters">Clear</button>
        </div>

        <div className="space-y-5">
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Keyword</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. sprung floor" className="mt-2 h-10" data-testid="filter-q" />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Any city" className="mt-2 h-10" data-testid="filter-city" />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Category</Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {CATS.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    category === c.key
                      ? "bg-[#FF4500] text-white border-[#FF4500]"
                      : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"
                  }`}
                  data-testid={`filter-cat-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Max $ / hour</Label>
              <span className="text-sm font-display font-bold">${maxPrice}{maxPrice >= 500 ? "+" : ""}</span>
            </div>
            <Slider min={10} max={500} step={5} value={[maxPrice]} onValueChange={(v) => setMaxPrice(v[0])} className="mt-4" data-testid="filter-price-slider" />
          </div>
          <Button onClick={apply} className="w-full bg-[#0A0A0A] hover:bg-neutral-800 text-white h-11 rounded-full" data-testid="apply-filters">Apply</Button>
        </div>
      </aside>

      {/* Results */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-1">RESULTS</div>
            <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">
              {loading ? "Loading…" : `${results.length} space${results.length === 1 ? "" : "s"}`}
            </h1>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {city && <Badge variant="outline" className="gap-1" data-testid="chip-city">{city}<X className="w-3 h-3 cursor-pointer" onClick={() => { setCity(""); setTimeout(apply, 0); }} /></Badge>}
            {category !== "all" && <Badge variant="outline" className="gap-1 capitalize">{category}<X className="w-3 h-3 cursor-pointer" onClick={() => { setCategory("all"); setTimeout(apply, 0); }} /></Badge>}
          </div>
        </div>
        {results.length === 0 && !loading ? (
          <div className="border border-dashed border-neutral-300 rounded-xl p-16 text-center">
            <div className="font-display font-bold text-xl">No spaces match your filters</div>
            <div className="text-sm text-neutral-500 mt-2">Try widening the price range or clearing the city.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {results.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}
      </section>
    </div>
  );
}
